import { PersonAdd } from '@mui/icons-material';
import { Button, Stack, type Theme, Typography } from '@mui/material';
import { useState } from 'react';
import Icon from '/@/components/icon';
import { useGrowersDashboard } from '/@/hooks/dashboards';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess, useIsValidatedAdvisor, useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import NewGrowersModalV2 from '/@/views/dashboard/growers/components/new-growers-modal-v2';

function FieldsEmpty({
    onClick,
    onAddFields,
    filtering = false,
    emptyMessage,
}: {
    onClick?: () => void;
    onAddFields?: () => void;
    filtering?: boolean;
    emptyMessage?: string;
}) {
    const showNewFieldButton = useHasWriteAccess();
    const [newGrowersModalOpen, setNewGrowersModalOpen] = useState(false);
    const isAdvisor = useIsValidatedAdvisor();
    const isRV = useIsValidatedRV();
    const isConsultant = useIsValidatedConsultant();
    const userNodeQuery = useOwnUserNode();
    const userNodeId = userNodeQuery.data?.id;
    const growersQuery = useGrowersDashboard({
        node: userNodeId,
        nodeType: 'grower',
        excludeTargetNode: true,
    });
    const hasGrowers = (growersQuery.data?.count ?? 0) > 0;
    const showNoGrowersEmpty =
        !filtering && (isAdvisor || isRV || isConsultant) && !hasGrowers && !growersQuery.isValidating && !!userNodeId;

    return (
        <Stack spacing={2} mt={4} alignItems='center'>
            {showNoGrowersEmpty ? (
                <PersonAdd sx={{ width: 36, height: 36 }} color='primary' />
            ) : (
                <Icon
                    type='fields2'
                    sx={{
                        width: 36,
                        height: 36,
                        '& svg *': {
                            fill: (t: Theme) => `${t.palette.primary.main} !important`,
                            fillOpacity: '1 !important',
                        },
                    }}
                />
            )}
            <Stack alignItems='center' textAlign='center'>
                {filtering ? (
                    <>
                        <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                            {_('No se encontraron resultados')}
                        </Typography>
                        <Typography color='primary.mui.inactive' variant='subtitle1'>
                            {_('No encontramos lotes que coincidan con estos criterios.')}
                        </Typography>
                    </>
                ) : showNoGrowersEmpty ? (
                    <>
                        <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                            {_('Agregá a los primeros productores')}
                        </Typography>
                        <Typography color='primary.mui.inactive' variant='subtitle1'>
                            {_(
                                'Vas a poder hacer un seguimiento de sus lotes y generar recomendaciones personalizadas.'
                            )}
                        </Typography>
                    </>
                ) : showNewFieldButton ? (
                    <>
                        <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                            {_('Agregá lotes para generar recomendaciones')}
                        </Typography>
                        <Typography color='primary.mui.inactive' variant='subtitle1'>
                            {_('Vas a poder generar recomendaciones personalizadas según las condiciones del lote.')}
                        </Typography>
                    </>
                ) : (
                    <Stack
                        direction='column'
                        alignItems='center'
                        justifyContent='center'
                        width='100%'
                        spacing={'0.5'}
                        px={4}
                    >
                        <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                            {emptyMessage}
                        </Typography>
                        <Typography color='primary.mui.inactive' variant='subtitle1'>
                            {_('Cuando se carguen, vas a poder hacer el seguimiento desde esta pantalla.')}
                        </Typography>
                    </Stack>
                )}
            </Stack>
            {showNoGrowersEmpty ? (
                <Button color='secondary' variant='contained' onClick={() => setNewGrowersModalOpen(true)}>
                    {_('Agregar productor')}
                </Button>
            ) : (
                showNewFieldButton &&
                onClick && (
                    <Button color='secondary' variant='contained' onClick={onClick}>
                        {_('Agregar lote')}
                    </Button>
                )
            )}
            {showNoGrowersEmpty && (
                <NewGrowersModalV2
                    open={newGrowersModalOpen}
                    setOpen={setNewGrowersModalOpen}
                    onAddFields={onAddFields ?? (() => {})}
                />
            )}
        </Stack>
    );
}

export default FieldsEmpty;
